import { useTranslation } from '@jq4race/i18n';
import type { ZoneResponse } from '@jq4race/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@jq4race/ui';
import { useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

interface DeactivateZoneDialogProps {
  zone: ZoneResponse | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export function DeactivateZoneDialog({ zone, onConfirm, onCancel }: DeactivateZoneDialogProps) {
  const { t } = useTranslation('zones');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!zone) {
      setSubmitting(false);
      setError(null);
    }
  }, [zone]);

  function confirm() {
    setSubmitting(true);
    setError(null);
    try {
      onConfirm();
      onCancel();
    } catch (err) {
      setError(translateApiError(err, t, { fallback: t('errors.generic') }));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={!!zone} onOpenChange={(next) => (!next ? onCancel() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('dialog.deactivateTitle')}</DialogTitle>
          <DialogDescription>{t('dialog.deactivateDescription')}</DialogDescription>
        </DialogHeader>

        <div className="mt-6 rounded-sm border border-jq-sand/70 bg-jq-surface-2/50 px-4 py-3">
          <p className="text-[12px] uppercase tracking-widest text-jq-graphite">
            {t('dialog.zoneLabel')}
          </p>
          <p className="mt-1 break-all font-mono text-[13px] text-jq-charcoal">{zone?.name}</p>
        </div>

        {error && (
          <p className="mt-4 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-3 py-2 text-[13px] text-jq-danger">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={submitting}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={confirm}
            disabled={submitting}
          >
            {submitting ? t('actions.deactivating') : t('actions.deactivate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
